import { Component, ViewChild, AfterViewInit, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LoginService } from 'src/app/auth/login.service';
import {
  DashboardFilterService,
  PriceRangeFilter,
} from 'src/app/core/services/dashboard-filter.service';
import { MobileMenuService } from 'src/app/core/services/mobile-menu.service';
import { ModalUploadComponent } from 'src/app/shared/components/modal-upload/modal-upload.component';
import { MediaListComponent } from '../../media/media-list/media-list.component';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css']
})
export class DashboardComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('uploadModal') uploadModal!: ModalUploadComponent;
  @ViewChild('mediaList') mediaListComponent!: MediaListComponent;

  isMobileMenuOpen = false;
  isMobileView = false;
  priceFilterValue = 0;
  sortOrder: 'ASC' | 'DESC' | null = null;
  private isViewInitialized = false;
  priceFilterOptions = [
    { value: 1, label: '0 - 10 triệu' },
    { value: 2, label: '10 triệu - 50 triệu' },
    { value: 3, label: '50 triệu - 100 triệu' },
    { value: 4, label: '100 triệu - 150 triệu' }
  ];

  filterSort = [
    { value: 'ASC', label: 'Tăng dần' },
    { value: 'DESC', label: 'Giảm dần' }
  ];

  private menuSub?: Subscription;
  private routerSub?: Subscription;

  constructor(
    public loginService: LoginService,
    private mobileMenuService: MobileMenuService,
    private dashboardFilterService: DashboardFilterService,
    public router: Router
  ) {}

  isMainStockActive(): boolean {
    if (!this.router || !this.router.url) return false;
    const url = this.router.url.split('?')[0];
    const isStocks = url === '/dashboard/stocks';
    const hasMyScope = this.router.url.includes('scope=my');
    return isStocks && !hasMyScope;
  }

  isMyStockActive(): boolean {
    if (!this.router || !this.router.url) return false;
    return this.router.url.startsWith('/dashboard/stocks') && this.router.url.includes('scope=my');
  }

  get showFooter(): boolean {
    return !this.shouldHideFooter(this.router?.url || '');
  }

  private shouldHideFooter(url: string): boolean {
    if (!url) return false;

    const path = url.split('?')[0];

    if (
      path === '/dashboard/user-stocks' ||
      path === '/dashboard/users' ||
      path === '/dashboard/requests' ||
      path === '/dashboard/benefit' ||
      path === '/dashboard/chat-admin'
    ) {
      return true;
    }

    if (path === '/dashboard/stocks' && url.includes('scope=my')) {
      return true;
    }

    return false;
  }

  get canViewRequestList(): boolean {
    return this.loginService.isLoggedIn() && this.loginService.getUserCode() === 'admin';
  }

  get canViewMyStock(): boolean {
    return this.loginService.isLoggedIn() && this.loginService.getUserCode() !== 'admin';
  }

  ngOnInit(): void {
    this.updateViewportMode();
    this.sortOrder = this.dashboardFilterService.currentSortOrder;
    this.menuSub = this.mobileMenuService.isOpen$.subscribe(isOpen => {
      this.isMobileMenuOpen = isOpen;
    });

    this.routerSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(event => {
        const url = (event as NavigationEnd).urlAfterRedirects || (event as NavigationEnd).url;
        
        if (!url.startsWith('/dashboard/stocks')) {
          this.resetFilters();
        }
      });
  }

  ngAfterViewInit(): void {
    this.isViewInitialized = true;
    // Reload media items ngay sau khi view init
    if (this.isMediaListRoute() && this.mediaListComponent && this.mediaListComponent.loadMediaItems) {
      this.mediaListComponent.loadMediaItems(true);
    }
  }

  ngOnDestroy(): void {
    this.menuSub?.unsubscribe();
    this.routerSub?.unsubscribe();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.updateViewportMode();
  }

  closeMobileMenu(): void {
    this.mobileMenuService.close();
  }

  onNonHomeNavClick(): void {
    this.resetFilters();
    this.closeMobileMenu();
  }

  onHomeClick(): void {
    this.resetFilters();
    if (this.isViewInitialized && this.mediaListComponent && this.mediaListComponent.loadMediaItems) {
      this.mediaListComponent.loadMediaItems(true);
    }
  }

  openUploadModal(): void {
    this.uploadModal.open();
  }

  onUploaded(files: any[]): void {
    if (this.mediaListComponent && this.mediaListComponent.reloadMoreMedia) {
      this.mediaListComponent.reloadMoreMedia();
    }
  }

  selectPriceRange(value: number): void {
    if (!this.isMediaListRoute()) {
      if (this.isMobileView) this.closeMobileMenu();
      return;
    }
    this.priceFilterValue = this.priceFilterValue === value ? 0 : value;
    this.dashboardFilterService.setPriceRange(
      this.priceFilterValue ? this.getPriceRange(this.priceFilterValue) : null
    );
    if (this.isMobileView) this.closeMobileMenu();
  }

  sort(value: string): void {
    if (!this.isMediaListRoute()) {
      if (this.isMobileView) this.closeMobileMenu();
      return;
    }
    const nextOrder = this.sortOrder === value ? null : (value as 'ASC' | 'DESC');
    this.filterSort = this.filterSort.map(option => ({
      ...option,
      active: option.value === nextOrder
    }));
    this.setSortOrder(nextOrder);
    if (this.isMobileView) this.closeMobileMenu();
  }

  isMediaListRoute(): boolean {
    return this.router.url.startsWith('/dashboard/stocks');
  }

  setSortOrder(order: 'ASC' | 'DESC' | null): void {
    this.sortOrder = order;
    this.dashboardFilterService.setSortOrder(order);
  }

  private resetFilters(): void {
    this.priceFilterValue = 0;
    this.dashboardFilterService.setPriceRange(null);
    this.setSortOrder(null);
    this.filterSort = this.filterSort.map(option => ({
      value: option.value,
      label: option.label
    }));
  }

  private getPriceRange(value: number): PriceRangeFilter {
    switch (value) {
      case 1:
        return { fromNumber: 0, toNumber: 10000000 };
      case 2:
        return { fromNumber: 10000000, toNumber: 50000000 };
      case 3:
        return { fromNumber: 50000000, toNumber: 100000000 };
      case 4:
        return { fromNumber: 100000000, toNumber: 150000000 };
      default:
        return {};
    }
  }

  private updateViewportMode(): void {
    this.isMobileView = window.innerWidth <= 768;
    if (!this.isMobileView) {
      this.mobileMenuService.close();
    }
  }
}
